import { useAuth0 } from "@auth0/auth0-react";
import { Field, FieldProps, Form, StandardFormSelect } from "collections/Form";
import { ModalForm } from "collections/Modal";
import { DISMISS, useNotification } from "collections/Notifications";
import { DetailsLoader, SubmitButton } from "elements";
import {
  TenantsListQuery,
  useAccessorQuery,
  useAddDeviceUserMutation,
  useDeleteDeviceUserMutation,
  useTenantsListQuery
} from "generated/graphql";
import { Link, Route, useHistory, useRouteMatch } from "polyrouter";
import { useTenantId } from "polyrouter/SelectedTenantProvider";
import React, { Dispatch, useEffect, useState } from "react";
import { useTranslation } from "utilities/i18n";
import { InferType, object, string } from "yup";

export const AccountSchema = object({
  account: string()
    .label("ACCOUNT")
    .required()
});
export type AccountType = InferType<typeof AccountSchema>;

export const AuthorizedTenants: React.FC<{
  tenantData: TenantsListQuery | undefined;
  setNewTenantInfo: Dispatch<
    React.SetStateAction<{ name: string; id: string } | null>
  >;
}> = ({ tenantData, setNewTenantInfo }) => {
  const { t } = useTranslation();
  const tenantId = useTenantId();
  const { user } = useAuth0();
  const currentUserId = user ? user.sub : "";

  const { data: accessorData, fetching: accessorFetching } = useAccessorQuery({
    variables: {
      accessorId: currentUserId
    }
  });

  const userRolesByTenantObj = accessorData?.accessor?.grants.reduce(
    (userRolesObj, currentGrant) => {
      const resourceId = currentGrant.resourceId;
      const roles = currentGrant.roles.map(role => role.name);
      return {
        ...userRolesObj,
        [resourceId]: roles
      };
    },
    {} as { [key: string]: Array<string> }
  );

  const availableTenants = tenantData?.tenants.reduce(
    (availableTenantsArr: any, currentTenant) => {
      if (
        userRolesByTenantObj &&
        userRolesByTenantObj[currentTenant.id]?.indexOf(
          "admin" || "it-admin"
        ) !== -1 &&
        currentTenant.id !== tenantId
      ) {
        return [
          ...availableTenantsArr,
          {
            text: currentTenant.name,
            value: currentTenant.id
          }
        ];
      }
      return [...availableTenantsArr];
    },
    [] as Array<{ text: string; value: string }>
  );

  return (
    <DetailsLoader loading={accessorFetching} title={t("LOADING")}>
      <div className="form-content">
        {availableTenants.length > 0 ? (
          <>
            <span data-testid="confirmation">
              {t("MIGRATE_DEVICE_USER_DETAILS")}
            </span>
            <StandardFormSelect
              data-testid="tenant-dropdown"
              label={t("ACCOUNT")}
              name="account"
              options={availableTenants || []}
              tabIndex={2}
              onSelectionChange={id =>
                setNewTenantInfo(newTenantInfo => {
                  return { ...newTenantInfo!, id };
                })
              }
              inline={true}
            />
          </>
        ) : (
          <span data-testid="no-accounts-migrate">
            {t("MIGRATE_DEVICE_USER_NO_ACCOUNTS")}
          </span>
        )}
      </div>
    </DetailsLoader>
  );
};

const MigrateDeviceUserModal: React.FC<{
  refresh: () => void;
  deviceUsers: Array<{ name: string; id: string }>;
}> = ({ refresh, deviceUsers }) => {
  const { t } = useTranslation();
  const [newTenantInfo, setNewTenantInfo] = useState<{
    name: string;
    id: string;
  } | null>(null);
  const [, addDeviceUser] = useAddDeviceUserMutation();
  const [, deleteDeviceUser] = useDeleteDeviceUserMutation();
  const [addNotification] = useNotification();
  const { url } = useRouteMatch();
  const cancelURL = url.replace("/migrate", "");
  const root = useRouteMatch().path;
  const { data: tenantData } = useTenantsListQuery();
  const history = useHistory();
  const tenantId = useTenantId();

  useEffect(() => {
    if (deviceUsers.length < 1) {
      history.push("/manage/device-users");
    }
  }, [deviceUsers.length, history]);

  const initialValues = {
    account: ""
  };

  const handleTenantName = () => {
    const tenant = tenantData?.tenants.find(
      tenant => tenant.id === newTenantInfo?.id
    );
    setNewTenantInfo({ ...newTenantInfo!, name: tenant?.name as string });
  };

  const onSubmit = async (values: AccountType) => {
    let success = true;
    for (const deviceUser of deviceUsers) {
      const removeUserResponse = await deleteDeviceUser({
        accessorId: deviceUser.id,
        resourceId: tenantId
      });
      if (removeUserResponse.error) {
        success = false;
        addNotification({
          type: "error",
          dismiss: DISMISS.DEFAULT,
          message: t("DEVICE_USERS_MIGRATED_ERROR", {
            message: removeUserResponse.error.message
          }),
          persistThroughNav: true
        });
      }
      const addUserResponse = await addDeviceUser({
        accessorId: deviceUser.id,
        resourceId: values.account
      });
      if (addUserResponse.error) {
        success = false;
        addNotification({
          type: "error",
          dismiss: DISMISS.DEFAULT,
          message: t("DEVICE_USERS_MIGRATED_ERROR", {
            message: addUserResponse.error.message
          }),
          persistThroughNav: true
        });
      }
    }
    if (success) {
      addNotification({
        type: "success",
        dismiss: DISMISS.DEFAULT,
        message: t("DEVICE_USERS_MIGRATED", {
          length: deviceUsers.length,
          name: newTenantInfo?.name
        }),
        persistThroughNav: true
      });
    }
    refresh();
  };

  return (
    <ModalForm
      title={t("MIGRATE_DEVICE_USER")}
      initialValues={initialValues}
      onSubmit={onSubmit}
      name={"MigrateDeviceUserModal"}
      validationSchema={AccountSchema}
    >
      {({ isSubmitting }) => (
        <Form data-testid="migrate-form">
          <Route exact path={root}>
            {deviceUsers.length > 0 && (
              <AuthorizedTenants
                tenantData={tenantData}
                setNewTenantInfo={setNewTenantInfo}
              />
            )}
            <div className="btn-container">
              {tenantData?.tenants !== undefined ? (
                <>
                  <Link
                    to={"/manage/device-users"}
                    className="btn"
                    data-testid="cancel-bttm"
                  >
                    {t("CANCEL")}
                  </Link>
                  <Link
                    nest
                    to="/confirm"
                    onClick={e =>
                      !newTenantInfo?.id
                        ? e.preventDefault()
                        : handleTenantName()
                    }
                    className={`btn btn-primary ${
                      !newTenantInfo?.id ? "disabled" : ""
                    }`}
                  >
                    {t("CONTINUE")}
                  </Link>
                </>
              ) : (
                <Link to={cancelURL} className="btn btn-primary">
                  {t("OK")}
                </Link>
              )}
            </div>
          </Route>
          <Route nest path="/confirm">
            <div className="form-content">
              <span data-testid="confirmation">
                {t("DEVICE_USERS_WILL_BE_MIGRATED", {
                  length: deviceUsers.length,
                  name: newTenantInfo?.name
                })}
              </span>
            </div>
            <div className="btn-container">
              <Link
                to={"/manage/device-users/migrate"}
                className="btn btn-secondary"
              >
                {t("BACK")}
              </Link>
              <Link to={"/manage/device-users"} className="btn">
                {t("CANCEL")}
              </Link>
              <Field name="submit">
                {({ form: { dirty, isSubmitting, isValid } }: FieldProps) => (
                  <SubmitButton
                    isSubmitting={isSubmitting}
                    testId="save"
                    disabled={!isValid || !dirty}
                    labelKey="CONTINUE"
                    submittingLabelKey="SAVING"
                  />
                )}
              </Field>
            </div>
          </Route>
        </Form>
      )}
    </ModalForm>
  );
};

export default MigrateDeviceUserModal;
